import Vue from 'vue';
import { passive } from '@/utils/helpers/promise';
import {
  showToastInLightspeed,
  registerToastCallback,
  pendingToastReferences,
  postMessageToLightspeed,
  deleteToastCallback,
} from '@/scaffolding/tko-lightspeed';

let nextCallbackId = 0;
const expls = ['Whoops!', 'Yikes!', 'Blimey!', 'Oh no!', 'Oh Dear!'];

const titleOrObject = (flash) => (typeof flash === 'string' ? { title: flash } : flash);

// Type mapping: Vuex → Lightspeed
const typeToLevel = {
  info: 'info',
  success: 'success',
  warn: 'warning',
  error: 'critical',
};

// Helper to send toast to Lightspeed
function sendToLightspeed({ type, title, msg, opts = {} }) {
  const level = typeToLevel[type] || 'info';
  // eslint-disable-next-line no-plusplus
  const callbackId = nextCallbackId++;
  const callbacks = [];

  const toastOptions = {
    level,
    title,
    message: msg || undefined,
    isDismissible: true,
    callbackId,
  };

  // Handle action button (including undo)
  // Note: Lightspeed will send back the property name ('action') not our callback identifier
  const actionCallbackName = `callback_twa_${callbackId}_action`;
  if (opts.undo) {
    toastOptions.actionText = Vue.t('Undo');
    toastOptions.action = actionCallbackName;
    registerToastCallback(actionCallbackName, opts.undo);
    callbacks.push(actionCallbackName);
  } else if (opts.action && opts.actionText) {
    // New cleaner pattern: { action: fn, actionText: 'Button Text' }
    toastOptions.actionText = opts.actionText;
    toastOptions.action = actionCallbackName;
    registerToastCallback(actionCallbackName, opts.action);
    callbacks.push(actionCallbackName);
  } else if (opts.showButton && opts.onClickButton && opts.buttonText) {
    // Legacy pattern: { showButton: true, onClickButton: fn, buttonText: '...' }
    toastOptions.actionText = opts.buttonText;
    toastOptions.action = actionCallbackName;
    registerToastCallback(actionCallbackName, opts.onClickButton);
    callbacks.push(actionCallbackName);
  }

  // Handle onRemove callback (called when toast is dismissed)
  // Note: Lightspeed will send back 'onRemove' not our callback identifier
  if (opts.onRemove) {
    const onRemoveCallbackName = `callback_twa_${callbackId}_onRemove`;
    toastOptions.onRemove = onRemoveCallbackName;
    registerToastCallback(onRemoveCallbackName, opts.onRemove);
    callbacks.push(onRemoveCallbackName);
  }

  // Create toast reference object
  const toastRef = {
    id: null,
    _callbackId: callbackId,
    _callbacks: callbacks,
    Remove() {
      if (!this.id) {
        return;
      }
      const toastId = this.id;
      this.id = null;
      // eslint-disable-next-line no-underscore-dangle
      this._callbacks.forEach((name) => deleteToastCallback(name));
      postMessageToLightspeed('twa:toast-remove', { toastId });
    },
  };

  // Store for later ID population
  pendingToastReferences.set(callbackId, toastRef);

  // Send toast via Lightspeed bridge
  showToastInLightspeed(toastOptions);

  return toastRef;
}

export default {
  namespaced: true,
  actions: {
    info: ({ dispatch }, flash) => dispatch('show', { ...titleOrObject(flash), type: 'info' }),
    warn: ({ dispatch }, flash) => dispatch('show', { ...titleOrObject(flash), type: 'warn' }),
    success: ({ dispatch }, flash) => dispatch('show', { ...titleOrObject(flash), type: 'success' }),
    error: ({ dispatch }, flash) => dispatch('show', { ...titleOrObject(flash), type: 'error' }),
    errorExclaim: ({ dispatch }, flash = {}) => {
      const title = expls[Math.floor(expls.length * Math.random())];
      dispatch('show', { ...flash, type: 'error', title });
    },

    show(context, { type, title, msg, opts = {} }) {
      // For undo, wrap callback to resolve promise when invoked
      if (opts.undo) {
        const undoPromise = passive();
        const originalUndo = opts.undo;
        const wrappedOpts = {
          ...opts,
          undo: () => {
            originalUndo();
            undoPromise.resolve();
          },
        };
        const toastRef = sendToLightspeed({ type, title, msg, opts: wrappedOpts });
        // Attach remove method to promise for consistency
        undoPromise.remove = () => toastRef.Remove();
        return undoPromise;
      }
      return sendToLightspeed({ type, title, msg, opts });
    },
  },
};
